/**
 * ACL-Editor fuer Kundenakten (Phase R4a-5).
 *
 * Wird genutzt in:
 *  - AdminAktenstrukturPage: Default-ACL pro Kategorie
 *  - Kundenakte-Inline-Override: Per-Kunde-ACL auf einzelner Sub-Akte
 *
 * Subjects (Personen + Gruppen) kommen aus
 * `GET /api/modules/bibliothek/acl-subjects`.
 *
 * `allowReset` = true bei Per-Kunde-Override: ein „Auf Default zuruecksetzen"-
 * Button uebergibt (null, null) an onSave, sodass das Backend die Override
 * loescht und die Vererbung wieder greift.
 *
 * Joerg 2026-06-02.
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, RotateCcw } from 'lucide-react';

interface Subject {
  id: string;
  name: string;
  kind: 'group' | 'person';
  description?: string | null;
}

interface SubjectsResponse {
  success: boolean;
  data: { groups: Subject[]; persons: Subject[] };
}

const BIBLIOTHEK_BASE = '/api/modules/bibliothek';

interface Props {
  open: boolean;
  title: string;
  /** Aktuelle readers. null = vererbt (bei Override) / nichts gesetzt (bei Default). */
  readers: string[] | null;
  authors: string[] | null;
  allowReset?: boolean;
  saving?: boolean;
  onClose: () => void;
  /** null = Reset auf Vererbung. */
  onSave: (readers: string[] | null, authors: string[] | null) => Promise<void> | void;
}

export default function AktenAclEditModal({ open, title, readers, authors, allowReset, saving, onClose, onSave }: Props) {
  const [allSubjects, setAllSubjects] = useState<Subject[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [readerIds, setReaderIds] = useState<string[]>(readers ?? []);
  const [authorIds, setAuthorIds] = useState<string[]>(authors ?? []);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReaderIds(readers ?? []);
    setAuthorIds(authors ?? []);
    setError(null);
  }, [open, readers, authors]);

  useEffect(() => {
    if (!open) return;
    setLoadingSubjects(true);
    fetch(`${BIBLIOTHEK_BASE}/acl-subjects`, { credentials: 'include' })
      .then((r) => r.json() as Promise<SubjectsResponse>)
      .then((d) => {
        if (d.success) {
          // Gruppen zuerst (in UI bevorzugt), dann Personen.
          setAllSubjects([...d.data.groups, ...d.data.persons]);
        }
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoadingSubjects(false));
  }, [open]);

  const subjectById = useMemo(() => {
    const map = new Map<string, Subject>();
    for (const s of allSubjects) map.set(s.id, s);
    return map;
  }, [allSubjects]);

  function toggle(list: string[], setter: (v: string[]) => void, id: string) {
    if (list.includes(id)) setter(list.filter((x) => x !== id));
    else setter([...list, id]);
  }

  async function handleSave(reset = false) {
    setError(null);
    try {
      if (reset) {
        await onSave(null, null);
      } else {
        await onSave(readerIds, authorIds);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-3xl max-h-[90dvh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h3 className="font-semibold text-slate-100 truncate">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200 p-1" aria-label="Schliessen">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {loadingSubjects ? (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : (
            <>
              <AclColumn
                title="Wer darf lesen (Reader)"
                description="Sieht die Sub-Akte und ihre Dokumente."
                subjects={allSubjects}
                selectedIds={readerIds}
                onToggle={(id) => toggle(readerIds, setReaderIds, id)}
              />
              <AclColumn
                title="Wer darf bearbeiten (Author)"
                description="Darf Dokumente in dieser Sub-Akte hochladen, editieren und löschen."
                subjects={allSubjects}
                selectedIds={authorIds}
                onToggle={(id) => toggle(authorIds, setAuthorIds, id)}
              />
            </>
          )}
          {error && <div className="text-sm text-rose-400">{error}</div>}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-slate-800">
          {allowReset ? (
            <button
              onClick={() => void handleSave(true)}
              disabled={saving}
              className="inline-flex items-center gap-2 px-3 py-2 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm disabled:opacity-50"
              title="Override entfernen — erbt wieder vom Default"
            >
              <RotateCcw size={14} /> Auf Default zurücksetzen
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm">
              Abbrechen
            </button>
            <button
              onClick={() => void handleSave(false)}
              disabled={saving || loadingSubjects}
              className="px-4 py-2 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Speichern
            </button>
          </div>
        </div>
      </div>
      {/* Hilfs-Helper fuer Render des subject-Namens bei Chips ohne explizite Subject-Liste */}
      {(() => { void subjectById; return null; })()}
    </div>
  );
}

interface ColumnProps {
  title: string;
  description: string;
  subjects: Subject[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

function AclColumn({ title, description, subjects, selectedIds, onToggle }: ColumnProps) {
  const groups = subjects.filter((s) => s.kind === 'group');
  const persons = subjects.filter((s) => s.kind === 'person');
  const [showPersons, setShowPersons] = useState(false);
  const [personSearch, setPersonSearch] = useState('');

  const filteredPersons = personSearch.trim()
    ? persons.filter((p) => p.name.toLowerCase().includes(personSearch.trim().toLowerCase()))
    : persons.slice(0, 30);

  return (
    <section className="space-y-2">
      <header>
        <h4 className="text-sm font-semibold text-slate-100">{title}</h4>
        <p className="text-xs text-slate-500">{description}</p>
      </header>

      <div className="space-y-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-2">Gruppen</div>
        <div className="flex flex-wrap gap-1.5">
          {groups.map((g) => {
            const on = selectedIds.includes(g.id);
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => onToggle(g.id)}
                className={`text-xs px-2 py-1 rounded border transition-colors ${
                  on
                    ? 'bg-brand-600/30 border-brand-600/60 text-brand-200'
                    : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                }`}
                title={g.description ?? undefined}
              >
                {g.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-1">
        <button
          type="button"
          onClick={() => setShowPersons((v) => !v)}
          className="text-[11px] uppercase tracking-wider text-slate-500 hover:text-slate-300 mt-2"
        >
          Einzelpersonen {showPersons ? '▾' : '▸'} {selectedIds.filter((id) => persons.some((p) => p.id === id)).length > 0 && (
            <span className="ml-1 text-brand-400 normal-case">
              ({selectedIds.filter((id) => persons.some((p) => p.id === id)).length} ausgewählt)
            </span>
          )}
        </button>
        {showPersons && (
          <div className="space-y-1">
            <input
              type="text"
              value={personSearch}
              onChange={(e) => setPersonSearch(e.target.value)}
              placeholder="Person suchen…"
              className="w-full px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-100 text-xs"
            />
            <div className="max-h-40 overflow-y-auto flex flex-wrap gap-1.5">
              {filteredPersons.map((p) => {
                const on = selectedIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onToggle(p.id)}
                    className={`text-xs px-2 py-1 rounded border transition-colors ${
                      on
                        ? 'bg-brand-600/30 border-brand-600/60 text-brand-200'
                        : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
            {personSearch.trim() === '' && persons.length > 30 && (
              <div className="text-[10px] text-slate-500">{persons.length - 30} weitere — bitte suchen.</div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
